"use client";

import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import type { CmsLocaleFragment } from "@workspace/casino_core/queries/CmsSidebarQuery";
import { setCookie } from "@workspace/cookies/browser";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SidebarMenuButton,
} from "@workspace/frontend-ui";
import { usePathname, useRouter } from "next/navigation";
import type { FC } from "react";
import { SIDEBAR_TOOLTIP_CLASS } from "../sidebarTooltip";

type Props = {
  locales: CmsLocaleFragment[];
  currentLocale: string;
};

const LanguageSwitcher: FC<Props> = ({ locales, currentLocale }) => {
  const router = useRouter();
  const pathname = usePathname();

  const active = locales.find((l) => l.code === currentLocale) ?? locales[0];

  const handleSelect = (code: string) => {
    setCookie("NEXT_LOCALE", code, { expires: 365, sameSite: "lax" });
    router.push(pathname);
    router.refresh();
  };

  if (!active) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          tooltip={{
            children: active.nativeLabel,
            className: SIDEBAR_TOOLTIP_CLASS,
          }}
          className="hover:text-sidebar-primary bg-sidebar-accent rounded-xl"
          size="md"
        >
          {active.icon?.url && (
            <img
              src={active.icon.url}
              alt={active.nativeLabel}
              width={20}
              height={20}
              loading="lazy"
              className="shrink-0 rounded-sm"
            />
          )}
          <span className="flex-1 truncate group-data-[collapsible=icon]:hidden">
            {active.nativeLabel}
          </span>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            className="ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/menu-button:rotate-180"
          />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        className="min-w-40 ring-0 shadow-[0_4px_40px_rgba(0,0,0,0.4)]"
      >
        {locales.map((locale) => (
          <DropdownMenuItem
            key={locale.code}
            onSelect={() => handleSelect(locale.code)}
            className="gap-2"
          >
            {locale.icon?.url && (
              <img
                src={locale.icon.url}
                alt={locale.nativeLabel}
                width={16}
                height={16}
                loading="lazy"
                className="shrink-0 rounded-sm"
              />
            )}
            {locale.nativeLabel}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
